import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
    AlertTriangle, BellRing, CheckCircle2, ChevronDown, ChevronUp, Crosshair, Info, Megaphone, Send, Users,
    TrendingUp,
} from "lucide-react";
import useApiMethods from "../../../lib/useApiMethods";
import ToastMsg from "../../../components/common/ToastMsg";
import { DASHBOARD_TEAM_NUDGE, DASHBOARD_TEAM_TODAY } from "../../../constants/api";
import { Skeleton } from "../../../components/ui/skeleton";
import { cn } from "../../../utils/tailwindUtils";

const SEVERITY = {
    high:   { icon: AlertTriangle, cls: "border-red-200 bg-red-50/70 dark:border-red-800 dark:bg-red-900/20", ic: "text-red-600 dark:text-red-400" },
    medium: { icon: AlertTriangle, cls: "border-amber-200 bg-amber-50/70 dark:border-amber-800 dark:bg-amber-900/20", ic: "text-amber-600 dark:text-amber-400" },
    low:    { icon: Info,          cls: "border-sky-200 bg-sky-50/70 dark:border-sky-800 dark:bg-sky-900/20", ic: "text-sky-600 dark:text-sky-400" },
    info:   { icon: CheckCircle2,  cls: "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800", ic: "text-brand-600 dark:text-brand-300" },
};

const STATE = {
    push:     { label: "Needs a push", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
    watch:    { label: "Watch",        cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" },
    on_track: { label: "On track",     cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
};

// One warning card. The button shows only when the viewer can use it: a link
// to a page their role or plan does not allow would just end on a 403.
const FocusItem = ({ f, busy, onAct, canUse }) => {
    const s = SEVERITY[f.severity] || SEVERITY.info;
    const Icon = s.icon;
    return (
        <div className={cn("flex items-start gap-3 rounded-xl border p-3", s.cls)}>
            <Icon className={cn("h-4 w-4 mt-0.5 flex-shrink-0", s.ic)} />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{f.title}</p>
                {f.detail && <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 break-words">{f.detail}</p>}
            </div>
            {f.action && canUse(f.action) && (
                <button type="button" onClick={onAct} disabled={busy}
                        className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-brand-700 dark:text-brand-300 hover:bg-gray-50 disabled:opacity-60">
                    {f.action.type === "nudge" && <BellRing className="h-3.5 w-3.5" />}
                    {busy ? "Sending…" : f.action.label}
                </button>
            )}
        </div>
    );
};

const Num = ({ v, bad }) => (
    <span className={cn("tabular-nums", v && bad ? "font-semibold text-red-600 dark:text-red-400" : v ? "text-gray-900 dark:text-gray-100" : "text-gray-300 dark:text-gray-600")}>
        {v || 0}
    </span>
);

// "Team today": the top of the manager dashboard. What the team should do
// today, who needs a push, and a button to push them (an in-app nudge).
const TeamToday = () => {
    const { fetch, create } = useApiMethods();
    const navigate = useNavigate();
    const { hasPermission, hasFeature } = useContext(AuthContext);
    const canUse = (a) => (!a.feature || hasFeature(a.feature))
        && (!a.permission || hasPermission(a.permission, "view"));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [denied, setDenied] = useState(false);
    const [sending, setSending] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [compose, setCompose] = useState(null);   // {user_ids, names} while writing a custom nudge
    const [message, setMessage] = useState("");

    const load = useCallback(() => {
        fetch(DASHBOARD_TEAM_TODAY)
            .then(res => {
                if (res?.status === "success") setData(res.data);
                else setDenied(true);
            })
            .catch(() => setDenied(true))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const nudge = (userIds, key, custom = "") => {
        setSending(key);
        create(DASHBOARD_TEAM_NUDGE, { user_ids: userIds, ...(custom && { message: custom }) })
            .then(res => {
                if (res?.status !== "success") throw new Error(res?.message);
                const sent = res.sent?.length || 0;
                const skipped = res.skipped?.length || 0;
                ToastMsg(sent ? "success" : "info",
                    sent ? `Nudged ${sent} rep${sent === 1 ? "" : "s"}${skipped ? ` · ${skipped} already nudged recently` : ""}`
                        : "Already nudged in the last 2 hours");
                setCompose(null);
                setMessage("");
                load();
            })
            .catch(() => ToastMsg("error", "Could not send the nudge"))
            .finally(() => setSending(null));
    };

    const act = (f, key) => {
        if (f.action?.type === "nudge") nudge(f.action.user_ids, key);
        else if (f.action?.route) navigate(f.action.route);
    };

    // Not a manager of this team: say nothing rather than show an error.
    if (denied) return null;

    const t = data?.totals || {};
    const board = data?.board || [];
    const toPush = board.filter(b => b.state === "push" && !b.is_me);
    const shown = showAll ? board : board.slice(0, 6);

    return (
        <section className="mb-4 space-y-3">
            {/* ── headline ── */}
            <div className="rounded-2xl p-4 text-white shadow-sm"
                 style={{ background: "linear-gradient(120deg, var(--brand-from, #9c5def), var(--brand-to, #bd56ea))" }}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <p className="text-xs opacity-80 flex items-center gap-1"><Crosshair className="h-3.5 w-3.5" /> Team today · {dayjs().format("dddd, D MMM")}</p>
                        <h2 className="text-lg md:text-xl font-semibold mt-0.5">
                            {loading ? "Reading the team's day…"
                                : t.reps_to_push ? `${t.reps_to_push} of ${t.reps} rep${t.reps === 1 ? "" : "s"} need${t.reps_to_push === 1 ? "s" : ""} a push today`
                                : "Everyone is on track today"}
                        </h2>
                        {!loading && (
                            <p className="text-sm opacity-90 mt-0.5">
                                {t.overdue || 0} overdue · {t.due_today || 0} due today · {t.untouched_24h || 0} new leads untouched 24h+ · {t.demos_today || 0} demos today · {t.activities_today || 0} activities logged so far
                            </p>
                        )}
                    </div>
                    {toPush.length > 0 && (
                        <button type="button" disabled={sending === "all"}
                                onClick={() => nudge(toPush.map(b => b.user_id), "all")}
                                className="inline-flex items-center gap-1.5 self-start md:self-auto rounded-lg bg-white text-brand-700 px-3 py-2 text-sm font-semibold shadow hover:bg-white/90 disabled:opacity-60">
                            <Megaphone className="h-4 w-4" />
                            {sending === "all" ? "Sending…" : `Nudge all ${toPush.length} who need it`}
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
                {/* ── focus list ── */}
                <div className="xl:col-span-5 space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 px-1">Focus today</h3>
                    {loading ? [0, 1, 2].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" />) :
                        (data?.focus || []).map((f, i) => (
                            <FocusItem key={i} f={f} canUse={canUse} busy={sending === `focus-${i}`}
                                       onAct={() => act(f, `focus-${i}`)} />
                        ))}

                    {/* Marketing: ad sync, spend with no leads, CPL spikes, paid
                        leads nobody called, failed conversion/WhatsApp sends. */}
                    {!loading && (data?.marketing || []).length > 0 && (
                        <>
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 px-1 pt-2 flex items-center gap-1.5">
                                <TrendingUp className="h-3.5 w-3.5" /> Marketing
                            </h3>
                            {data.marketing.map((f, i) => (
                                <FocusItem key={`m${i}`} f={f} canUse={canUse} busy={sending === `mkt-${i}`}
                                           onAct={() => act(f, `mkt-${i}`)} />
                            ))}
                        </>
                    )}
                </div>

                {/* ── rep board ── */}
                <div className="xl:col-span-7 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/80 dark:border-gray-700 shadow-sm p-3 min-w-0">
                    <div className="flex items-center justify-between px-1 mb-2">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                            <Users className="h-3.5 w-3.5" /> Rep board
                        </h3>
                        <span className="text-[11px] text-gray-400">neediest first</span>
                    </div>
                    {loading ? <Skeleton className="h-40 w-full" /> : board.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-8">No reps with leads yet.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-[11px] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                        <th className="py-1.5 pr-2 font-medium">Rep</th>
                                        <th className="py-1.5 px-1 font-medium text-center" title="Overdue follow-ups">Overdue</th>
                                        <th className="py-1.5 px-1 font-medium text-center" title="Follow-ups due today">Today</th>
                                        <th className="py-1.5 px-1 font-medium text-center" title="New leads untouched for 24h+">Untouched</th>
                                        <th className="py-1.5 px-1 font-medium text-center" title="Demos today">Demos</th>
                                        <th className="py-1.5 px-1 font-medium text-center" title="Activities logged today">Logged</th>
                                        <th className="py-1.5 px-1 font-medium text-center" title="Won this month / target">Won</th>
                                        <th className="py-1.5 pl-1" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {shown.map(b => {
                                        const s = STATE[b.state];
                                        return (
                                            <tr key={b.user_id} className="border-b border-gray-50 dark:border-gray-700/50 align-top">
                                                <td className="py-2 pr-2 min-w-[160px]">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className="font-semibold text-gray-900 dark:text-white">{b.name}</span>
                                                        {b.is_me && <span className="text-[10px] text-gray-400">(you)</span>}
                                                        <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded", s.cls)}>{s.label}</span>
                                                        {!b.is_active_user && <span className="text-[10px] text-red-500">inactive</span>}
                                                    </div>
                                                    {b.reasons.length > 0 && (
                                                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{b.reasons.join(" · ")}</p>
                                                    )}
                                                    <p className="text-[10px] text-gray-400 mt-0.5">
                                                        {b.last_activity_at ? `last active ${dayjs(b.last_activity_at).fromNow()}` : "no activity in 30 days"}
                                                        {b.last_nudged_at && ` · nudged ${dayjs(b.last_nudged_at).fromNow()}`}
                                                    </p>
                                                </td>
                                                <td className="py-2 px-1 text-center"><Num v={b.overdue} bad /></td>
                                                <td className="py-2 px-1 text-center"><Num v={b.due_today} /></td>
                                                <td className="py-2 px-1 text-center"><Num v={b.untouched_24h} bad /></td>
                                                <td className="py-2 px-1 text-center"><Num v={b.demos_today} /></td>
                                                <td className="py-2 px-1 text-center"><Num v={b.activities_today} /></td>
                                                <td className="py-2 px-1 text-center tabular-nums text-gray-700 dark:text-gray-200">
                                                    {b.month.won}{b.month.closures_target ? <span className="text-gray-400">/{b.month.closures_target}</span> : ""}
                                                </td>
                                                <td className="py-2 pl-1 text-right whitespace-nowrap">
                                                    {!b.is_me && <>
                                                    <button type="button" disabled={sending === b.user_id}
                                                            onClick={() => nudge([b.user_id], b.user_id)}
                                                            title="Send this rep their focus for today"
                                                            className={cn("inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold border",
                                                                b.state === "push"
                                                                    ? "bg-brand-600 text-white border-brand-600 hover:bg-brand-700"
                                                                    : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700")}>
                                                        <BellRing className="h-3 w-3" /> {sending === b.user_id ? "…" : "Nudge"}
                                                    </button>
                                                    <button type="button" title="Write your own message"
                                                            onClick={() => { setCompose({ user_ids: [b.user_id], names: b.name }); setMessage(""); }}
                                                            className="ml-1 inline-flex items-center rounded-lg px-1.5 py-1 border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700">
                                                        <Send className="h-3 w-3" />
                                                    </button>
                                                    </>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {board.length > 6 && (
                        <button type="button" onClick={() => setShowAll(s => !s)}
                                className="mt-2 w-full flex items-center justify-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-300">
                            {showAll ? <>Show fewer <ChevronUp className="h-3.5 w-3.5" /></> : <>Show all {board.length} reps <ChevronDown className="h-3.5 w-3.5" /></>}
                        </button>
                    )}
                </div>
            </div>

            {/* ── custom message ── */}
            {compose && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setCompose(null)}>
                    <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-4 shadow-xl" onClick={e => e.stopPropagation()}>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Message to {compose.names}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Leave it blank to send their focus list for today.</p>
                        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} maxLength={1000}
                                  autoFocus placeholder="e.g. Close the Apex proposal before 5 pm today."
                                  className="mt-3 w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                        <div className="flex justify-end gap-2 mt-3">
                            <button type="button" onClick={() => setCompose(null)}
                                    className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">Cancel</button>
                            <button type="button" disabled={sending === "compose"}
                                    onClick={() => nudge(compose.user_ids, "compose", message.trim())}
                                    className="px-3 py-1.5 text-xs rounded-lg bg-brand-600 text-white font-semibold hover:bg-brand-700 disabled:opacity-60">
                                {sending === "compose" ? "Sending…" : "Send nudge"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default TeamToday;
